import * as Yup from "yup";
import { dateToyyyyMMdd } from "../../../util/date";
import { getPatchOperations } from "../../../util/form";
import {
  MakeOptional,
  PersonTilsynserklaering,
} from "../../../interfaces/schema";
import { validationMessages } from "../../../constants/validation";

export const personTilsynerklaeringForm = (
  personId: string,
  tilsynserklaeringId: string | undefined,
  te?: PersonTilsynserklaering,
): FormConfig<PersonTilsynerklaeringDTO> => {
  const isLocked = !!te?.erklModtaget;
  return {
    title: "tilsynserklæring",
    lockedValues: [
      { id: "personId", value: personId },
      { id: "tilsynserklaeringId", value: tilsynserklaeringId },
    ],
    validationSchema: Yup.object({
      erklAar: Yup.number()
        .required("Erklæringsår skal være sat")
        .typeError("Erklæringsår skal være et tal")
        .test(
          "len",
          "Ugyldigt erklæringsår",
          (value) => value?.toString().length === 4,
        )
        .min(1950, "Ugyldigt erklæringsår"),
      tilsynserklaeringStatus: Yup.string().required("Status skal være sat"),
      erklModtaget: Yup.date()
        .nullable()
        .when("tilsynserklaeringStatus", {
          is: (value: string) => value !== "FristOrdinaer",
          then: (schema) => schema.required(validationMessages.required),
        }),
    }),
    form: [
      {
        component: "textinput",
        id: "erklAar",
        label: "Erklæringsår",
        initialValue: "",
        locked: true,
        isRequired: true,
      },
      {
        component: "kode-select",
        kodeListeType: "TilsynserklaeringStatusKode",
        id: "tilsynserklaeringStatus",
        label: "Status",
        placeholder: "Vælg status",
        locked: false,
        initialValue: "",
        isRequired: true,
        filter: isLocked ? ["FristOrdinaer"] : undefined,
      },
      {
        component: "datepicker",
        id: "erklModtaget",
        label: "Modtaget dato",
        locked: isLocked,
        initialValue: "",
        isRequired: true,
        conditionalHidden: ({ values }) =>
          values.tilsynserklaeringStatus === "FristOrdinaer" ||
          values.tilsynserklaeringStatus === "",
      },
      {
        component: "datepicker",
        id: "frist",
        label: "Frist",
        initialValue: "",
        isRequired: true,
        locked: true,
      },
    ],
    get: `person/${personId}/tilsynserklaeringer/${tilsynserklaeringId}`,
    getDocument: `person/${personId}/tilsynserklaeringer/${tilsynserklaeringId}/dokument`,
    downloadLink: `person/${personId}/tilsynserklaeringer/${tilsynserklaeringId}/download`,
    post(values) {
      const { erklAar, erklModtaget, tilsynserklaeringStatus, frist } = values;
      const isFristOrdinaer = tilsynserklaeringStatus === "FristOrdinaer";
      return {
        query: `person/${personId}/tilsynserklaeringer/`,
        body: {
          erklAar: erklAar.toString(),
          erklModtaget:
            erklModtaget && !isFristOrdinaer
              ? dateToyyyyMMdd(erklModtaget)
              : null,
          tilsynserklaeringStatus: tilsynserklaeringStatus.toString(),
          frist: dateToyyyyMMdd(frist),
        },
      };
    },
    patch(values) {
      if (!values.tilsynserklaeringStatus) {
        delete (
          values as MakeOptional<PersonTilsynerklaeringDTO, "erklModtaget">
        ).erklModtaget;
      }
      return {
        query: `person/${personId}/tilsynserklaeringer/${tilsynserklaeringId}`,
        body: JSON.stringify(getPatchOperations(values)),
      };
    },
    delete() {
      return {
        query: `person/${personId}/tilsynserklaeringer/${tilsynserklaeringId}`,
      };
    },
  };
};
